import { useState, useEffect, useCallback } from 'react';
import { Tag, Plus, Calendar, Percent, X, Loader2, Ban } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';

interface Promotion {
  id: string;
  product_name: string;
  desconto_percentual: number;
  preco_original: number;
  preco_promocional: number;
  data_inicio: string;
  data_fim: string;
  ativo: boolean;
  motivo: string;
}

interface ProductResult {
  id: string;
  name: string;
  sale_price: number;
  barcode: string;
}

export function PromotionsPage() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newPromo, setNewPromo] = useState({
    product_id: '',
    desconto_percentual: 20,
    data_inicio: new Date().toISOString().split('T')[0]!,
    data_fim: '',
  });

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<ProductResult[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductResult | null>(null);
  const [searchingProducts, setSearchingProducts] = useState(false);

  useEffect(() => {
    fetchPromotions();
  }, []);

  async function fetchPromotions() {
    try {
      const res = await apiFetch('/api/promotions?include_expired=true');
      if (res.ok) {
        const data = await res.json();
        setPromotions(data.data || []);
      }
    } catch {
      toast.error('Erro ao carregar promoções');
    } finally {
      setLoading(false);
    }
  }

  const searchProducts = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setProductResults([]);
      return;
    }
    setSearchingProducts(true);
    try {
      const res = await apiFetch(`/api/products?search=${encodeURIComponent(term)}&limit=5`);
      if (res.ok) {
        const data = await res.json();
        setProductResults(data.data || []);
      }
    } catch {
      // Silently fail
    } finally {
      setSearchingProducts(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch && !selectedProduct) {
        searchProducts(productSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts, selectedProduct]);

  const handleCreate = async () => {
    if (!newPromo.product_id || !newPromo.data_fim) {
      toast.error('Preencha todos os campos');
      return;
    }
    try {
      const res = await apiFetch('/api/promotions', {
        method: 'POST',
        body: JSON.stringify({
          product_id: newPromo.product_id,
          desconto_percentual: newPromo.desconto_percentual,
          data_inicio: newPromo.data_inicio,
          data_fim: newPromo.data_fim,
        }),
      });
      if (res.ok) {
        toast.success('Promoção criada!');
        setShowCreate(false);
        setNewPromo({ product_id: '', desconto_percentual: 20, data_inicio: new Date().toISOString().split('T')[0]!, data_fim: '' });
        setSelectedProduct(null);
        setProductSearch('');
        await fetchPromotions();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao criar promoção');
      }
    } catch {
      toast.error('Erro ao criar promoção');
    }
  };

  async function handleDeactivate(promoId: string) {
    const res = await apiFetch(`/api/promotions/${promoId}`, {
      method: 'PATCH',
      body: JSON.stringify({ ativo: false }),
    });
    if (res.ok) {
      toast.success('Promoção encerrada');
      await fetchPromotions();
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Promoções</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie promoções vinculadas a alertas de validade
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Nova Promoção
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Active Promotions */}
      {!loading && (
        <div className="space-y-3">
          {promotions.map((promo) => (
            <div
              key={promo.id}
              className="rounded-xl border border-border bg-card/50 backdrop-blur-sm p-4 transition-all hover:bg-card/80"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-emerald-500/10 p-2">
                    <Tag className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground">{promo.product_name}</h3>
                    <p className="text-xs text-muted-foreground">{promo.motivo}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground line-through">
                        R${Number(promo.preco_original).toFixed(2)}
                      </span>
                      <span className="text-sm font-bold text-emerald-500">
                        R${Number(promo.preco_promocional).toFixed(2)}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-500">
                      <Percent className="h-3 w-3" />
                      {promo.desconto_percentual}% OFF
                    </span>
                  </div>
                  {promo.ativo && (
                    <button
                      onClick={() => handleDeactivate(promo.id)}
                      className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
                      title="Encerrar promoção"
                    >
                      <Ban className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(promo.data_inicio).toLocaleDateString('pt-BR')} -{' '}
                  {new Date(promo.data_fim).toLocaleDateString('pt-BR')}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 ${
                    promo.ativo ? 'bg-emerald-500/10 text-emerald-500' : 'bg-zinc-500/10 text-zinc-500'
                  }`}
                >
                  {promo.ativo ? 'Ativa' : 'Encerrada'}
                </span>
              </div>
            </div>
          ))}

          {promotions.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <Tag className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Nenhuma promoção ativa</p>
              <p className="text-xs text-muted-foreground">
                Crie promoções a partir dos alertas de validade
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Nova Promoção</h2>
              <button onClick={() => { setShowCreate(false); setSelectedProduct(null); setProductSearch(''); setProductResults([]); }} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <label className="text-sm text-muted-foreground">Produto</label>
                <input
                  type="text"
                  placeholder="Buscar produto..."
                  value={selectedProduct ? selectedProduct.name : productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setSelectedProduct(null);
                    setNewPromo({ ...newPromo, product_id: '' });
                  }}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
                {searchingProducts && (
                  <div className="absolute right-3 top-8">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {productResults.length > 0 && !selectedProduct && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-40 overflow-y-auto">
                    {productResults.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => {
                          setSelectedProduct(product);
                          setNewPromo({ ...newPromo, product_id: product.id });
                          setProductResults([]);
                          setProductSearch('');
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted/50 transition-colors"
                      >
                        <span className="font-medium text-foreground">{product.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          R${Number(product.sale_price).toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm text-muted-foreground">Desconto (%)</label>
                <input
                  type="number"
                  value={newPromo.desconto_percentual}
                  onChange={(e) =>
                    setNewPromo({ ...newPromo, desconto_percentual: Number(e.target.value) })
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  min={1}
                  max={100}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground">Data Início</label>
                  <input
                    type="date"
                    value={newPromo.data_inicio}
                    onChange={(e) => setNewPromo({ ...newPromo, data_inicio: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Data Fim</label>
                  <input
                    type="date"
                    value={newPromo.data_fim}
                    onChange={(e) => setNewPromo({ ...newPromo, data_fim: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  />
                </div>
              </div>

              <button
                onClick={handleCreate}
                className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                Criar Promoção
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
